function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); return Constructor; }

import { presetLoader, presetsTypes } from '@wix/fedops-presets';
import { ConsentPolicyAccessor } from '@wix/consent-policy-client-accessor';
import { env } from './env';
import performance from './performance/performance';
import Times from './times/times';
import DataSource from './data-source/data-source';
import sessionManager from './session-manager/session-manager';
import LoadingPhases, { CODE_PARSING_PHASE_NAME } from './loading-phases/loading-phases';
import { create as createReporter } from './reporter/reporter-factory';
import { DataItems } from './data-items/data-items';
import { phasesConfigValues } from './loading-phases/loading-phases-config';
import { generateGuid } from './utils';

var callHook = function callHook(hook, hookArgs) {
  if (hook) {
    return hook(hookArgs);
  }
};
/**
 * Base Logger
 * Before adding new parameters to the reported events, please make sure the BI schema supports them:
 * http://bo.wix.com/bi-catalog-webapp/#/sources/72
 */


var BaseLogger = /*#__PURE__*/function () {
  function BaseLogger(appName, appVersion, reporter, params) {
    var defaultParams = {
      appId: null,
      widgetId: null,
      metasiteId: null,
      sessionId: sessionManager.getSessionId(),
      isServerSide: null,
      disableAutoLoadFinish: false,
      phasesConfig: phasesConfigValues.SEND_ON_START,
      interactionTimeout: null,
      timeoutHook: null,
      startHook: null,
      endHook: null,
      isPersistent: false,
      corrId: generateGuid(),
      presetType: presetsTypes.DEFAULT,
      customParams: {},
      paramsOverrides: {}
    };
    var paramsWithDefaults = Object.assign({}, defaultParams, params);
    this.appName = appName;
    this.appVersion = appVersion;
    this.appId = paramsWithDefaults.appId;
    this.widgetId = paramsWithDefaults.widgetId;
    this.metasiteId = paramsWithDefaults.metasiteId;
    this.corrId = paramsWithDefaults.corrId;
    this.isServerSide = paramsWithDefaults.isServerSide;
    this.params = {
      sessionId: paramsWithDefaults.sessionId
    };
    this._customParams = Object.assign({}, paramsWithDefaults.customParams);
    this.disableAutoLoadFinish = paramsWithDefaults.disableAutoLoadFinish;
    this.phasesConfig = paramsWithDefaults.phasesConfig;
    this._appLoadedCalled = {};
    this._appStartLoadCalled = {};
    this._presetType = paramsWithDefaults.presetType;
    this._preset = presetLoader(this._presetType);
    this.dataItems = new DataItems(this.sessionId, this._preset);
    this.reporter = reporter || createReporter({
      preset: this._preset
    });
    this.times = new Times(appName);
    this.loadingPhases = new LoadingPhases(appName, this.times);
    this.dataSourceBase = new DataSource();
    this.dataSourceBase.addItem(this.dataItems.appName({
      appName: appName,
      isServerSide: this.isServerSide
    })).addItem(this.dataItems.dataItem({
      corrId: this.corrId
    })).addItem(this.dataItems.dataItem(paramsWithDefaults.paramsOverrides));

    if (params && params.artifactData) {
      this.dataSourceBase.addItem(this.dataItems.artifact(params.artifactData));
    }

    this._outgoingInteractions = {};
    this.interactionTimeout = paramsWithDefaults.interactionTimeout;
    this.timeoutHook = paramsWithDefaults.timeoutHook;
    this.startHook = paramsWithDefaults.startHook;
    this.endHook = paramsWithDefaults.endHook;
    this.isPersistent = paramsWithDefaults.isPersistent;

    if (paramsWithDefaults.reportBlackbox) {
      this._handleBlackboxPerformance();
    }
  }

  var _proto = BaseLogger.prototype;

  _proto._report = function _report(dataSource, reporterEndpoint) {
    return this.reporter.report(dataSource.mergeItems(), reporterEndpoint);
  };

  _proto.getAppName = function getAppName() {
    return this.appName;
  };

  _proto.getAppVersion = function getAppVersion() {
    return this.appVersion;
  };

  _proto.getReporter = function getReporter() {
    return this.reporter;
  };

  _proto.getParam = function getParam(paramName) {
    return this.params[paramName];
  };

  _proto._isDisableAutoLoadFinish = function _isDisableAutoLoadFinish() {
    return this.disableAutoLoadFinish;
  };

  _proto.isDisableAutoLoadFinish = function isDisableAutoLoadFinish() {
    return this._isDisableAutoLoadFinish();
  };

  _proto.reportNetworkAnalysis = function reportNetworkAnalysis(_ref) {
    var _this = this;

    var appId = _ref.appId;
    var resources = performance.getResources();
    setTimeout(function () {
      try {
        var resourceDataItem = _this.dataItems.resource();

        var analysis = resourceDataItem.performNetworkAnalysis(resources);

        if (!analysis) {
          return;
        }

        var dataSource = _this.dataSource.addItem(resourceDataItem).addItem(analysis);

        if (appId) {
          _this._changeAppNameForEvent(dataSource, appId);
        }

        _this._report(dataSource);
      } catch (e) {
        console.error('[fedops] Failed to report resource metrics', e);
      }
    }, 1000);
  }
  /**
   * Manual report for widget load start - Should only be used if your widget doesn't have its own .vm / .ejs file
   * Call this method at the earliest point possible when your widget starts loading.
   */
  ;

  _proto.appLoadStarted = function appLoadStarted(_temp) {
    var _ref2 = _temp === void 0 ? {} : _temp,
        appId = _ref2.appId,
        paramsOverrides = _ref2.paramsOverrides;

    if (appId && this._appStartLoadCalled[appId] || !appId && this._appStartLoadCalled[this.appName]) {
      return;
    }

    BaseLogger._markAppLoadStarted(this.appName);

    this._appStartLoadCalled[appId || this.appName] = true;
    this.times.setLoadStarted({
      appId: appId
    });
    var dataSource = this.dataSource.addItem(this.dataItems.biAppLoadStart()).addItem(this.dataItems.appContext({
      appId: appId || this.appId,
      widgetId: this.widgetId,
      isServerSide: this.isServerSide
    })).addItem(this.dataItems.dataItem(paramsOverrides));

    if (appId) {
      this._changeAppNameForEvent(dataSource, appId);
    }

    return this._report(dataSource, this._getEndpoint());
  };

  _proto._shouldAddCustomParams = function _shouldAddCustomParams(customParams) {
    var isEmptyObject = function isEmptyObject(obj) {
      return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
    };

    var bothCustomParamsAreEmpty = isEmptyObject(this._customParams) && isEmptyObject(customParams);
    var consentPolicyAccessor = new ConsentPolicyAccessor();

    var _consentPolicyAccesso = consentPolicyAccessor.getCurrentConsentPolicy(),
        functional = _consentPolicyAccesso.functional,
        analytics = _consentPolicyAccesso.analytics;

    var consentAllowsCustomParams = functional && analytics;
    return !bothCustomParamsAreEmpty && consentAllowsCustomParams;
  };

  _proto._addCustomParamsToEvent = function _addCustomParamsToEvent(dataSource, customParams) {
    var customParamsObj = typeof customParams === 'string' ? JSON.parse(customParams) : customParams;
    customParams = Object.assign({}, this._customParams, customParamsObj);

    if (Object.keys(customParams).length > 0) {
      var stringifiedCustomParams = JSON.stringify(customParams);
      dataSource.addItem(this.dataItems.customParams(stringifiedCustomParams));
    }
  }
  /**
   * Final phase. Call this method when your application finishes loading and after all custom phases.
   */
  ;

  _proto.appLoaded = function appLoaded(_temp2) {
    var _ref3 = _temp2 === void 0 ? {} : _temp2,
        appId = _ref3.appId,
        customParams = _ref3.customParams,
        paramsOverrides = _ref3.paramsOverrides;

    if (appId && this._appLoadedCalled[appId] || !appId && this._appLoadedCalled[this.appName]) {
      return;
    }

    BaseLogger._clearLoadTimeout();

    BaseLogger._markAndMeasureAppLoad(this.appName);

    this._appLoadedCalled[appId || this.appName] = true;

    this._sendLastAppLoadPhaseIfNeeded();

    this.reportNetworkAnalysis({
      appId: appId
    });
    var dataSource = this.dataSource.addItem(this.dataItems.biAppLoadFinish()).addItem(this.dataItems.appContext({
      appId: appId || this.appId,
      widgetId: this.widgetId,
      isServerSide: this.isServerSide
    })).addItem(this.dataItems.duration(this.times.getAppLoadTime({
      appId: appId
    })).setFirstRequestDuration(this.times.getFirstRequestDuration())).addItem(this.dataItems.dataItem(paramsOverrides));

    if (appId) {
      this._changeAppNameForEvent(dataSource, appId);
    }

    if (this._shouldAddCustomParams(customParams)) {
      this._addCustomParamsToEvent(dataSource, customParams);
    }

    return this._report(dataSource, this._getEndpoint());
  }
  /**
   * Start of an app loading phase, reports end of previous phase if configured
   */
  ;

  _proto.appLoadingPhaseStart = function appLoadingPhaseStart(name, _temp3) {
    var _ref4 = _temp3 === void 0 ? {} : _temp3,
        appId = _ref4.appId,
        widgetId = _ref4.widgetId,
        paramsOverrides = _ref4.paramsOverrides;

    this.loadingPhases.createCodeParsingPhaseIfNotExist({
      appId: appId,
      widgetId: widgetId
    });
    this.loadingPhases.saveLoadingPhase({
      name: name,
      appId: appId,
      widgetId: widgetId
    });

    this._sendPreviousPhaseIfNeeded(name, {
      appId: appId,
      widgetId: widgetId,
      paramsOverrides: paramsOverrides
    });

    performance.mark("[fedops] " + name + " started");
    var dataSource = this.dataSource.addItem(this.dataItems.biLoadPhaseStart()).addItem(this.dataItems.appContext({
      appId: appId,
      widgetId: widgetId
    })).addItem(this.dataItems.loadingPhaseStart({
      name: name
    })).addItem(this.dataItems.dataItem(paramsOverrides));

    if (appId) {
      this._changeAppNameForEvent(dataSource, appId);
    }

    this._report(dataSource, this._getEndpoint());
  };

  _proto._changeAppNameForEvent = function _changeAppNameForEvent(dataSource, appName) {
    dataSource.addItem(this.dataItems.appName({
      appName: appName,
      isServerSide: this.isServerSide
    }));
  };

  _proto._sendPreviousPhaseIfNeeded = function _sendPreviousPhaseIfNeeded(name, _temp4) {
    var _ref5 = _temp4 === void 0 ? {} : _temp4,
        appId = _ref5.appId,
        widgetId = _ref5.widgetId,
        paramsOverrides = _ref5.paramsOverrides;

    var previousPhase = this.loadingPhases.getPhasePreviousTo({
      name: name,
      appId: appId,
      widgetId: widgetId
    });

    if (this.phasesConfig === phasesConfigValues.SEND_ON_START || previousPhase && previousPhase.name === CODE_PARSING_PHASE_NAME) {
      performance.mark("[fedops] " + previousPhase.name + " finished");
      var loadingPhaseDataItem = this.dataItems.loadingPhaseFinish(previousPhase);
      var loadingPhaseBiItem = this.dataItems.biLoadPhaseFinish();
      var overridesDataItem = this.dataItems.dataItem(paramsOverrides);
      var dataSource = this.dataSource.addItem(loadingPhaseDataItem).addItem(loadingPhaseBiItem).addItem(overridesDataItem);

      this._report(dataSource);
    }
  }
  /**
   * End of an app loading phase, reports end of previous phase
   */
  ;

  _proto.appLoadingPhaseFinish = function appLoadingPhaseFinish(name, _temp5) {
    var _ref6 = _temp5 === void 0 ? {} : _temp5,
        appId = _ref6.appId,
        widgetId = _ref6.widgetId,
        widgetArray = _ref6.widgetArray,
        paramsOverrides = _ref6.paramsOverrides;

    if (this.phasesConfig === phasesConfigValues.SEND_ON_START) {
      throw new Error('To use appLoadingPhaseFinish you must use "phasesConfig: SEND_START_AND_FINISH" setting');
    }

    performance.mark("[fedops] " + name + " finished");
    var loadingPhase = this.loadingPhases.getAppLoadingPhaseData({
      name: name,
      appId: appId,
      widgetId: widgetId
    });

    if (!loadingPhase) {
      throw new Error("Cannot report end of a phase that wasn't started. Phase " + name + " doesn't exist");
    }

    var loadingPhaseDataItem = this.dataItems.loadingPhaseFinish(loadingPhase);
    var loadingPhaseBiItem = this.dataItems.biLoadPhaseFinish();
    var overridesDataItem = this.dataItems.dataItem(paramsOverrides);
    var eventContextDataItem = this.dataItems.appContext({
      appId: appId,
      widgetId: widgetId,
      widgetArray: widgetArray,
      isServerSide: this.isServerSide
    });
    var dataSource = this.dataSource.addItem(loadingPhaseDataItem).addItem(loadingPhaseBiItem).addItem(eventContextDataItem).addItem(overridesDataItem);

    if (appId) {
      this._changeAppNameForEvent(dataSource, appId);
    }

    return this._report(dataSource, this._getEndpoint());
  };

  _proto._getEndpoint = function _getEndpoint() {
    return this.isPersistent ? this._preset.persistentEndpoint : this._preset.nonPersistentEndpoint;
  } // only for backwards compatibility purposes until we eliminate phasesConfig and SEND_ON_START
  ;

  _proto._sendLastAppLoadPhaseIfNeeded = function _sendLastAppLoadPhaseIfNeeded() {
    var appLoadingPhase = this.loadingPhases.getNextPhaseToReport();
    var shouldReportLastPhase = this.phasesConfig === phasesConfigValues.SEND_ON_START && appLoadingPhase && appLoadingPhase.name !== CODE_PARSING_PHASE_NAME;

    if (shouldReportLastPhase) {
      this._report(this.dataSource.addItem(this.dataItems.loadingPhaseFinish(appLoadingPhase)).addItem(this.dataItems.biLoadPhaseFinish()));
    }
  };

  BaseLogger._clearLoadTimeout = function _clearLoadTimeout() {
    if (env() && env().fedops && typeof env().fedops.clearLoadTimeout === 'function') {
      env().fedops.clearLoadTimeout();
    }
  };

  _proto.clearResourceTimings = function clearResourceTimings() {
    performance.clearResourceTimings();
  };

  BaseLogger._markAppLoadStarted = function _markAppLoadStarted(appName) {
    performance.mark("[fedops] " + appName + " app-load-started");
  };

  BaseLogger._markAndMeasureAppLoad = function _markAndMeasureAppLoad(appName) {
    performance.mark("[fedops] " + appName + " app-loaded");

    try {
      performance.measure("[fedops] " + appName + " app-loaded", "[fedops] " + appName + " app-load-started", "[fedops] " + appName + " app-loaded");
    } catch (e) {}
  };

  BaseLogger._markInteractionStarted = function _markInteractionStarted(interactionName) {
    performance.mark("[fedops] " + interactionName + " started");
  };

  BaseLogger._markAndMeasureInteractionEnded = function _markAndMeasureInteractionEnded(interactionName, outgoingInteraction) {
    if (outgoingInteraction) {
      performance.mark("[fedops] " + interactionName + " ended");

      try {
        performance.measure("[fedops] " + interactionName + " duration", "[fedops] " + interactionName + " started", "[fedops] " + interactionName + " ended");
      } catch (e) {}
    }
  }
  /**
   * Custom interaction start (http://bo.wix.com/bi-catalog-webapp/#/sources/72/events/)
   */
  ;

  _proto.interactionStarted = function interactionStarted(interactionName, _temp6) {
    var _this2 = this;

    var _ref7 = _temp6 === void 0 ? {} : _temp6,
        timeOverride = _ref7.timeOverride,
        interactionTimeout = _ref7.interactionTimeout,
        startHook = _ref7.startHook,
        timeoutHook = _ref7.timeoutHook,
        customParams = _ref7.customParams,
        paramsOverrides = _ref7.paramsOverrides;

    BaseLogger._markInteractionStarted(interactionName);

    var scheduleTimeoutEvent = function scheduleTimeoutEvent() {
      if (interactionTimeout || _this2.interactionTimeout) {
        return setTimeout(function () {
          var dataSource = _this2.dataSource.addItem({
            interactionName: interactionName
          }).addItem({
            errorType: 'timeout'
          }).addItem(_this2.dataItems.biError());

          _this2._report(dataSource);

          callHook(timeoutHook || _this2.timeoutHook, {
            name: interactionName,
            timeout: interactionTimeout || _this2.interactionTimeout
          });
        }, interactionTimeout || _this2.interactionTimeout);
      }
    };

    var dataSource = this.dataSource.addItem({
      name: interactionName
    }).addItem(this.dataItems.biInteractionStart()).addItem(this.dataItems.dataItem(paramsOverrides));

    if (this._shouldAddCustomParams(customParams)) {
      this._addCustomParamsToEvent(dataSource, customParams);
    }

    this._outgoingInteractions[interactionName] = {
      timestamp: timeOverride ? timeOverride : performance.now(),
      timeout: interactionTimeout || this.interactionTimeout
    };

    this._report(dataSource);

    callHook(startHook || this.startHook, {
      name: interactionName
    });
    var timeoutId = scheduleTimeoutEvent();
    return {
      timeoutId: timeoutId
    };
  }
  /**
   * Custom interaction end (http://bo.wix.com/bi-catalog-webapp/#/sources/72/events/)
   */
  ;

  _proto.interactionEnded = function interactionEnded(interactionName, _temp7) {
    var _this3 = this;

    var _ref8 = _temp7 === void 0 ? {} : _temp7,
        timeOverride = _ref8.timeOverride,
        timeoutId = _ref8.timeoutId,
        endHook = _ref8.endHook,
        customParams = _ref8.customParams,
        paramsOverrides = _ref8.paramsOverrides;

    var outgoingInteraction = this._outgoingInteractions[interactionName];

    BaseLogger._markAndMeasureInteractionEnded(interactionName, outgoingInteraction);

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    var getInteractionFromWindowIfPresent = function getInteractionFromWindowIfPresent(actualEnv) {
      return actualEnv.fedops && actualEnv.fedops.apps && actualEnv.fedops.apps[_this3.appName] && actualEnv.fedops.apps[_this3.appName].interactions && actualEnv.fedops.apps[_this3.appName].interactions[interactionName];
    };

    var interaction = this._outgoingInteractions[interactionName] || getInteractionFromWindowIfPresent(env());
    var endTime = timeOverride ? timeOverride : performance.now();
    var duration = interaction ? Math.floor(endTime - interaction.timestamp) : '';
    callHook(endHook || this.endHook, {
      name: interactionName,
      duration: duration,
      timeout: this._outgoingInteractions[interactionName] && this._outgoingInteractions[interactionName].timeout
    });
    var dataSource = this.dataSource.addItem({
      name: interactionName
    }).addItem({
      duration: duration
    }).addItem(this.dataItems.biInteractionEnd()).addItem(this.dataItems.dataItem(paramsOverrides));

    if (this._shouldAddCustomParams(customParams)) {
      this._addCustomParamsToEvent(dataSource, customParams);
    }

    delete this._outgoingInteractions[interactionName];

    this._report(dataSource);
  };

  _proto.flush = function flush() {
    this.reporter.flush();
  };

  _proto._handleBlackboxPerformance = function _handleBlackboxPerformance(pageMeasurement) {
    var _this4 = this;

    var actualEnv = env();
    var wixPerformanceMeasurements = actualEnv.wixPerformanceMeasurements;

    if (wixPerformanceMeasurements) {
      this._handleBlackboxPerformanceEntries(wixPerformanceMeasurements);
    } else if (pageMeasurement) {
      this._sendBlackboxMeasurement(pageMeasurement);
    } else {
      if (actualEnv.addEventListener) {
        actualEnv.addEventListener('wixPerformanceMeasurements', function () {
          return _this4._handleBlackboxPerformance();
        });
        actualEnv.addEventListener('wixPageMeasurements', function (event) {
          return _this4._handleBlackboxPerformance(event.detail);
        });
      }
    }
  };

  _proto._sendBlackboxMeasurement = function _sendBlackboxMeasurement(measurement) {
    var BLACKBOX_ENDPOINT = 'bpm';
    var blackboxBiDataItem = this.dataItems.biBlackbox(measurement);
    var blackboxDataItem = this.dataItems.blackboxPerformance(measurement);

    if (blackboxBiDataItem) {
      var dataSource = new DataSource().addItem(blackboxBiDataItem).addItem(blackboxDataItem);

      this._report(dataSource, BLACKBOX_ENDPOINT);
    }
  };

  _proto._handleBlackboxPerformanceEntries = function _handleBlackboxPerformanceEntries(measurements) {
    var _this5 = this;

    measurements.forEach(function (performanceEntryPromise) {
      performanceEntryPromise.then && performanceEntryPromise.then(function (performanceEntry) {
        return _this5._sendBlackboxMeasurement(performanceEntry);
      });
    });
  };

  _proto.getLoggerForWidget = function getLoggerForWidget(_ref9) {
    var appName = _ref9.appName,
        appId = _ref9.appId,
        widgetId = _ref9.widgetId,
        version = _ref9.version,
        timeoutHook = _ref9.timeoutHook,
        startHook = _ref9.startHook,
        endHook = _ref9.endHook,
        useGlobalLogger = _ref9.useGlobalLogger,
        paramsOverrides = _ref9.paramsOverrides,
        phasesConfig = _ref9.phasesConfig;
    var widgetAppName = appName || appId + "_" + widgetId;
    var widgetVersion = version || this.getAppVersion();
    var reporter = useGlobalLogger ? this.reporter : createReporter({
      biLoggerFactory: this.reporter._factory,
      preset: this._preset
    });
    var config = {
      isServerSide: this.isServerSide,
      appId: appId,
      widgetId: widgetId,
      sessionId: this.sessionId,
      phasesConfig: phasesConfig || phasesConfigValues.SEND_ON_START,
      isPersistent: this.isPersistent,
      timeoutHook: timeoutHook,
      startHook: startHook,
      endHook: endHook,
      customParams: this._customParams,
      presetType: this._presetType,
      paramsOverrides: paramsOverrides
    };
    return new BaseLogger(widgetAppName, widgetVersion, reporter, config);
  };

  _createClass(BaseLogger, [{
    key: "dataSource",
    get: function get() {
      return this.dataSourceBase.clone();
    }
  }, {
    key: "sessionId",
    get: function get() {
      return this.getParam('sessionId');
    },
    set: function set(sessionId) {
      this.params['sessionId'] = sessionId;
    }
  }]);

  return BaseLogger;
}();

export { BaseLogger as default };